/**
 * AcademyCalendar — month grid, colored event dots, click-to-drill-down.
 * Used on BOTH the Student dashboard (read-only) and Teacher dashboard
 * (with + Add / edit / delete controls when `canEdit` prop is true).
 *
 * Highlights the current day. Grade filter narrows which events surface.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, X, ExternalLink, Calendar as CalendarIcon, MapPin, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('academy_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAY_LABELS  = ['S','M','T','W','T','F','S'];

const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const dayRangeContainsEvent = (dayISO, event) => {
  if (!event.end_date) return event.date === dayISO;
  return dayISO >= event.date && dayISO <= event.end_date;
};

const CATEGORIES = [
  { id: 'field_trip', color: '#F97316', label: 'Field Trip' },
  { id: 'holiday',    color: '#22C55E', label: 'Holiday' },
  { id: 'quiz',       color: '#EAB308', label: 'Quiz / Test' },
  { id: 'assembly',   color: '#A78BFA', label: 'Assembly' },
  { id: 'deadline',   color: '#EF4444', label: 'Deadline' },
  { id: 'meeting',    color: '#22D3EE', label: 'Meeting' },
  { id: 'school_day', color: '#C9A961', label: 'Milestone' },
  { id: 'other',      color: '#71717A', label: 'Other' },
];

const EventForm = ({ initial, onSave, onCancel, onDelete }) => {
  const [form, setForm] = useState({
    title: initial?.title || '',
    date: initial?.date || fmtISO(new Date()),
    end_date: initial?.end_date || '',
    category: initial?.category || 'other',
    grades: initial?.grades || ['all'],
    description: initial?.description || '',
    location: initial?.location || '',
    link_url: initial?.link_url || '',
  });
  const [saving, setSaving] = useState(false);
  const isNew = !initial?.id;

  const handleSubmit = async () => {
    if (!form.title || !form.date) { toast.error('Title and date are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, end_date: form.end_date || undefined };
      const res = isNew
        ? await api.post('/academy/calendar/events', payload, { headers: authHeaders() })
        : await api.patch(`/academy/calendar/events/${initial.id}`, payload, { headers: authHeaders() });
      toast.success(isNew ? 'Event added' : 'Event updated');
      onSave(res.data.event);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${initial.title}"?`)) return;
    try {
      await api.delete(`/academy/calendar/events/${initial.id}`, { headers: authHeaders() });
      toast.success('Event deleted');
      onDelete(initial.id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-3 text-xs" data-testid="event-form">
      <input
        className="w-full bg-black border border-zinc-800 p-2 text-white text-sm"
        placeholder="Event title (required)"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        data-testid="event-title-input"
      />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[9px] font-black tracking-widest text-zinc-500 mb-1">START DATE</div>
          <input type="date" className="w-full bg-black border border-zinc-800 p-2 text-white" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" />
        </div>
        <div>
          <div className="text-[9px] font-black tracking-widest text-zinc-500 mb-1">END DATE (optional)</div>
          <input type="date" className="w-full bg-black border border-zinc-800 p-2 text-white" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
      </div>
      <div>
        <div className="text-[9px] font-black tracking-widest text-zinc-500 mb-1">CATEGORY</div>
        <select className="w-full bg-black border border-zinc-800 p-2 text-white" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="event-category-select">
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[9px] font-black tracking-widest text-zinc-500 mb-1">GRADES (comma-separated, or &quot;all&quot;)</div>
        <input className="w-full bg-black border border-zinc-800 p-2 text-white" placeholder="all  OR  4,5,6" value={(form.grades || []).join(',')} onChange={(e) => setForm({ ...form, grades: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
      </div>
      <input className="w-full bg-black border border-zinc-800 p-2 text-white" placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
      <input className="w-full bg-black border border-zinc-800 p-2 text-white" placeholder="Link (optional) — e.g. /academy/field-trip/ifly-2026-09-29" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
      <textarea className="w-full bg-black border border-zinc-800 p-2 text-white" placeholder="Description (optional)" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

      <div className="flex items-center justify-between pt-2">
        {!isNew ? (
          <button onClick={handleDelete} className="text-[10px] font-black tracking-widest text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="event-delete-btn">
            <Trash2 className="w-3 h-3" /> DELETE
          </button>
        ) : <span />}
        <div className="flex gap-2">
          <button onClick={onCancel} className="text-[10px] font-black tracking-widest text-zinc-500 hover:text-white px-3 py-1.5 border border-zinc-800">CANCEL</button>
          <button onClick={handleSubmit} disabled={saving} className="text-[10px] font-black tracking-widest text-black bg-amber-500 hover:bg-amber-400 disabled:opacity-50 px-3 py-1.5 inline-flex items-center gap-1" data-testid="event-save-btn">
            {saving && <Loader2 className="w-3 h-3 animate-spin" />} {isNew ? 'ADD EVENT' : 'SAVE'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AcademyCalendar = ({ canEdit = false, gradeFilter = null, title = 'Calendar' }) => {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => new Date(2026, 8, 1)); // Sept 2026 default so field trip is visible
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [editing, setEditing] = useState(null); // event object or "new"

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (gradeFilter) params.set('grade', String(gradeFilter));
      const res = await api.get(`/academy/calendar/events?${params}`, { headers: authHeaders() });
      setEvents(res.data.events || []);
    } catch (err) {
      // Silent fail — student view shouldn't crash the whole dashboard
      setEvents([]);
    } finally { setLoading(false); }
  }, [gradeFilter]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const monthDays = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const firstDay = new Date(y, m, 1);
    const startWeekday = firstDay.getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const eventsForDay = useCallback((day) => {
    if (!day) return [];
    const iso = fmtISO(day);
    return events.filter((e) => dayRangeContainsEvent(iso, e));
  }, [events]);

  const today = new Date();
  const todayISO = fmtISO(today);

  return (
    <div className="border-2 border-zinc-800 bg-black" data-testid="academy-calendar">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b-2 border-zinc-800">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" style={{ color: '#C9A961' }} />
          <span className="text-[11px] font-black tracking-[0.25em] uppercase text-white">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1 hover:bg-zinc-900" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4 text-zinc-400" />
          </button>
          <div className="text-xs font-black text-white min-w-[130px] text-center">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1 hover:bg-zinc-900" data-testid="cal-next">
            <ChevronRight className="w-4 h-4 text-zinc-400" />
          </button>
          {canEdit && (
            <button onClick={() => setEditing('new')} className="ml-2 text-[10px] font-black tracking-widest bg-amber-500 text-black px-2 py-1 hover:bg-amber-400 inline-flex items-center gap-1" data-testid="cal-add-event">
              <Plus className="w-3 h-3" /> ADD
            </button>
          )}
        </div>
      </div>

      {/* Weekday header row */}
      <div className="grid grid-cols-7 border-b border-zinc-900">
        {DAY_LABELS.map((d, i) => (
          <div key={i} className="text-[9px] font-black tracking-widest text-zinc-500 text-center py-1.5">{d}</div>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
      ) : (
        <div className="grid grid-cols-7">
          {monthDays.map((day, i) => {
            const evs = day ? eventsForDay(day) : [];
            const isToday = day && isSameDay(day, today);
            const isSelected = day && selectedDay && isSameDay(day, selectedDay);
            return (
              <button
                key={i}
                onClick={() => day && setSelectedDay(day)}
                disabled={!day}
                className={`aspect-square border-r border-b border-zinc-900 p-1.5 text-left transition-colors ${!day ? 'bg-zinc-950/50' : 'hover:bg-zinc-900/60'} ${isSelected ? 'bg-amber-500/10' : ''}`}
                data-testid={day ? `cal-day-${fmtISO(day)}` : undefined}
              >
                {day && (
                  <>
                    <div className={`text-[11px] font-black ${isToday ? 'text-black bg-amber-500 rounded-full w-5 h-5 flex items-center justify-center' : 'text-white'}`}>
                      {day.getDate()}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-0.5">
                      {evs.slice(0, 4).map((e) => (
                        <span key={e.id} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />
                      ))}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="p-2 border-t-2 border-zinc-800 flex flex-wrap gap-x-3 gap-y-1">
        {CATEGORIES.map((c) => (
          <div key={c.id} className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
            <span className="text-[8px] font-black tracking-widest text-zinc-500 uppercase">{c.label}</span>
          </div>
        ))}
      </div>

      {/* Selected-day detail drawer */}
      {selectedDay && (
        <div className="border-t-2 border-amber-500/40 bg-zinc-950 p-3" data-testid="cal-day-detail">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-black tracking-[0.25em] uppercase" style={{ color: '#C9A961' }}>
              {selectedDay.toDateString()} {selectedDay.toDateString() === today.toDateString() && '· TODAY'}
            </div>
            <button onClick={() => setSelectedDay(null)} className="text-zinc-500 hover:text-white text-xs" data-testid="cal-day-close"><X className="w-3.5 h-3.5" /></button>
          </div>
          {eventsForDay(selectedDay).length === 0 ? (
            <div className="text-[11px] text-zinc-500 italic">No events on this day.</div>
          ) : (
            <div className="space-y-2">
              {eventsForDay(selectedDay).map((e) => (
                <div key={e.id} className="border border-zinc-800 p-2 bg-black/60" data-testid={`cal-event-${e.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: e.color }} />
                        <span className="text-xs font-black text-white truncate">{e.title}</span>
                      </div>
                      <div className="text-[9px] font-black tracking-widest text-zinc-500 mt-0.5 uppercase">
                        {e.category_label}
                        {e.grades && e.grades.length > 0 && ` · Grades: ${e.grades.join(', ')}`}
                      </div>
                      {e.location && <div className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</div>}
                      {e.description && <div className="text-[10px] text-zinc-300 mt-1 leading-relaxed">{e.description}</div>}
                      {e.link_url && (
                        <button
                          onClick={() => { if (e.link_url.startsWith('/')) navigate(e.link_url); else window.open(e.link_url, '_blank'); }}
                          className="mt-2 text-[10px] font-black tracking-widest text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                          data-testid={`cal-event-link-${e.id}`}
                        >
                          OPEN DETAILS <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {canEdit && !e.is_virtual && (
                      <button onClick={() => setEditing(e)} className="text-[9px] font-black tracking-widest text-zinc-500 hover:text-white px-2 py-1 border border-zinc-800 shrink-0" data-testid={`cal-event-edit-${e.id}`}>EDIT</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit/Add modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-950 border-2 border-amber-500/60 max-w-lg w-full max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-black tracking-[0.28em] uppercase" style={{ color: '#C9A961' }}>
                {editing === 'new' ? 'Add Event' : 'Edit Event'}
              </div>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <EventForm
              initial={editing === 'new' ? null : editing}
              onSave={() => { setEditing(null); loadEvents(); }}
              onCancel={() => setEditing(null)}
              onDelete={() => { setEditing(null); loadEvents(); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyCalendar;
